/**
 * Stock occupancy helpers for the vehicle owner calendar.
 * Uses inclusive date-string comparison (YYYY-MM-DD) aligned with
 * backend unavailable_dates validation (full-day overlap).
 */

export const dateInRange = (strDate, start, end) =>
  strDate >= start && strDate <= end;

export const rangesOverlap = (startA, endA, startB, endB) =>
  new Date(startA) <= new Date(endB) && new Date(endA) >= new Date(startB);

/**
 * Sum quantities for ranges that cover a given day.
 * @param {string} strDate
 * @param {Array<{ start: string, end: string, quantity?: number }>} ranges
 */
export const sumQuantityForDay = (strDate, ranges) =>
  (ranges || []).reduce((sum, range) => {
    if (!range?.start || !range?.end) return sum;
    if (!dateInRange(strDate, range.start, range.end)) return sum;
    return sum + (Number(range.quantity) || 1);
  }, 0);

/**
 * Sum quantities for ranges overlapping a date window.
 * @param {string} start
 * @param {string} end
 * @param {Array<{ start: string, end: string, quantity?: number }>} ranges
 */
export const sumQuantityOverlapping = (start, end, ranges) =>
  (ranges || []).reduce((sum, range) => {
    if (!range?.start || !range?.end) return sum;
    if (!rangesOverlap(start, end, range.start, range.end)) return sum;
    return sum + (Number(range.quantity) || 1);
  }, 0);

/**
 * @param {string} strDate
 * @param {{ stock: number, reservations: Array, ownerBlocks: Array }} params
 */
export const getDayOccupancy = (strDate, { stock, reservations, ownerBlocks }) => {
  const booked = sumQuantityForDay(strDate, reservations);
  const blocked = sumQuantityForDay(strDate, ownerBlocks);
  const consumed = booked + blocked;
  const available = Math.max(stock - consumed, 0);

  return { booked, blocked, consumed, available };
};

/**
 * Max units the owner can block for a range (frontend guard; backend validates booked only).
 * @param {string} start
 * @param {string} end
 * @param {{ stock: number, reservations: Array, ownerBlocks: Array, excludeRange?: { start: string, end: string } }} params
 */
export const maxBlockableForRange = (
  start,
  end,
  { stock, reservations, ownerBlocks, excludeRange }
) => {
  const bookedQty = sumQuantityOverlapping(start, end, reservations);

  const otherBlocksQty = (ownerBlocks || []).reduce((sum, block) => {
    if (!block?.start || !block?.end) return sum;
    if (
      excludeRange &&
      block.start === excludeRange.start &&
      block.end === excludeRange.end
    ) {
      return sum;
    }
    if (!rangesOverlap(start, end, block.start, block.end)) return sum;
    return sum + (Number(block.quantity) || 1);
  }, 0);

  return Math.max(0, stock - bookedQty - otherBlocksQty);
};

/**
 * Normalize API booked dates into occupancy ranges.
 * @param {Array<{ startDate: string, endDate: string, quantity?: number }>} bookedDates
 * @param {(value: string) => string} formatDate
 */
export const normalizeReservations = (bookedDates, formatDate) =>
  (bookedDates || [])
    .filter((d) => d && d.startDate && d.endDate)
    .map((d) => ({
      start: formatDate(d.startDate),
      end: formatDate(d.endDate),
      quantity: Number(d.quantity) || 1,
    }));

/** Normalize owner block tuples for stable comparison / API payload. */
export const normalizeOwnerBlockTuples = (ranges) => {
  const seen = new Set();
  return (ranges || [])
    .filter((range) => range?.length >= 2 && range[0] && range[1])
    .filter(([start, end]) => {
      const key = `${start}|${end}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(([start, end, qty]) => [start, end, Number(qty) || 1])
    .sort(
      (a, b) =>
        a[0].localeCompare(b[0]) ||
        a[1].localeCompare(b[1]) ||
        a[2] - b[2]
    );
};

export const ownerBlockTuplesEqual = (a, b) =>
  JSON.stringify(normalizeOwnerBlockTuples(a)) ===
  JSON.stringify(normalizeOwnerBlockTuples(b));

export const ownerBlocksFromApi = (dateRanges, defaultStock, formatDate) =>
  (dateRanges || []).map((d) => [
    formatDate(d.startDate),
    formatDate(d.endDate),
    Number(d.quantity) || defaultStock,
  ]);

/**
 * Drop or clamp owner blocks that exceed stock minus reservations (matches API rules).
 */
export const sanitizeOwnerBlocksForSave = (ranges, stock, reservations) => {
  const normalized = normalizeOwnerBlockTuples(ranges);
  const saved = [];

  normalized.forEach(([start, end, qty]) => {
    const otherBlocks = saved.map(([s, e, q]) => ({
      start: s,
      end: e,
      quantity: q,
    }));
    const max = maxBlockableForRange(start, end, {
      stock,
      reservations,
      ownerBlocks: otherBlocks,
      excludeRange: { start, end },
    });
    if (max <= 0) return;

    saved.push([start, end, Math.min(qty, max)]);
  });

  return saved;
};

